/**
 * ThemeSystem — Dynamic UI theming based on context.
 *
 * Adjusts UI accent colors based on:
 * - Biome (desert warm, tundra cool, jungle green, nether red, end purple)
 * - Time of day (sunset = warm, night = cool)
 * - Dimension (overworld/nether/end palettes)
 *
 * Injects CSS variables that other components can use:
 *   var(--fw-accent), var(--fw-bg), var(--fw-text)
 */

const THEMES = {
  default:   { accent: '#FFD700', bg: 'rgba(30,30,30,0.92)', border: '#4A4A4A', text: '#DDD' },
  desert:    { accent: '#FFA726', bg: 'rgba(60,40,20,0.92)', border: '#8B6914', text: '#FFE4B5' },
  tundra:    { accent: '#81D4FA', bg: 'rgba(20,30,50,0.92)', border: '#3A5A80', text: '#E3F2FD' },
  jungle:    { accent: '#66BB6A', bg: 'rgba(20,40,25,0.92)', border: '#3A6A40', text: '#E8F5E9' },
  forest:    { accent: '#8BC34A', bg: 'rgba(25,35,25,0.92)', border: '#4A6B3A', text: '#F1F8E9' },
  swamp:     { accent: '#9CCC65', bg: 'rgba(30,40,30,0.92)', border: '#5A7A4A', text: '#F0F4C3' },
  mountains: { accent: '#B0BEC5', bg: 'rgba(40,45,50,0.92)', border: '#6A7A84', text: '#ECEFF1' },
  savanna:   { accent: '#D4A017', bg: 'rgba(50,40,20,0.92)', border: '#7A6A3A', text: '#FFF8E1' },
  plains:    { accent: '#CDDC39', bg: 'rgba(40,45,25,0.92)', border: '#6A7A4A', text: '#F9FBE7' },
  nether:    { accent: '#FF5722', bg: 'rgba(60,15,10,0.92)', border: '#8B1A0A', text: '#FFCCBC' },
  end:       { accent: '#BA68C8', bg: 'rgba(30,10,40,0.92)', border: '#6A1A8A', text: '#E1BEE7' },
  night:     { accent: '#7E57C2', bg: 'rgba(15,15,30,0.92)', border: '#3A3A5A', text: '#D1C4E9' },
};

export class ThemeSystem {
  constructor() {
    this.currentTheme = 'default';
    this._injectCSS();
    this.setTheme('default');
  }

  _injectCSS() {
    if (document.getElementById('fedeworld-theme')) return;
    const style = document.createElement('style');
    style.id = 'fedeworld-theme';
    style.textContent = `
      :root {
        --fw-accent: #FFD700;
        --fw-bg: rgba(30,30,30,0.92);
        --fw-border: #4A4A4A;
        --fw-text: #DDD;
        --fw-accent-transition: color 0.6s ease, background-color 0.6s ease, border-color 0.6s ease;
      }
      .fw-themed {
        background: var(--fw-bg);
        border-color: var(--fw-border);
        color: var(--fw-text);
        transition: var(--fw-accent-transition);
      }
      .fw-themed-accent {
        color: var(--fw-accent);
        transition: color 0.6s ease;
      }
    `;
    document.head.appendChild(style);
  }

  setTheme(themeName) {
    const theme = THEMES[themeName] || THEMES.default;
    if (this.currentTheme === themeName) return;
    this.currentTheme = themeName;
    const root = document.documentElement;
    root.style.setProperty('--fw-accent', theme.accent);
    root.style.setProperty('--fw-bg', theme.bg);
    root.style.setProperty('--fw-border', theme.border);
    root.style.setProperty('--fw-text', theme.text);
  }

  /**
   * Update theme based on player environment.
   * @param {string} dimension - 'overworld' | 'nether' | 'end'
   * @param {number|string} biome - biome id or name
   * @param {boolean} isNight
   */
  updateFromContext(dimension, biome, isNight) {
    let theme = 'default';
    if (dimension === 'nether') theme = 'nether';
    else if (dimension === 'end') theme = 'end';
    else if (isNight) theme = 'night';
    else {
      const biomeMap = {
        0: 'plains',     plains: 'plains',
        1: 'desert',     desert: 'desert',
        2: 'forest',     forest: 'forest',
        3: 'tundra',     tundra: 'tundra',
        4: 'mountains',  mountains: 'mountains',
        5: 'jungle',     jungle: 'jungle',
        6: 'swamp',      swamp: 'swamp',
        7: 'savanna',    savanna: 'savanna',
      };
      theme = biomeMap[biome] || 'default';
    }
    this.setTheme(theme);
  }

  getCurrentAccent() {
    return THEMES[this.currentTheme]?.accent || '#FFD700';
  }
}

export const themeSystem = new ThemeSystem();
